// The single place that decides "which transaction sets get explained": it
// composes parse850Segments and parse852Segments over an envelope tree
// (lib/envelope.ts's output), attaching parsed850 and/or parsed852 to each
// transaction. Every downstream consumer (the reducer, the validator, the
// sections builder, the UI) imports its "explained transaction" type from
// here rather than importing parser850.ts or parser852.ts directly, so
// adding a third transaction set later only means widening this one module.
//
// A transaction whose ST01 is neither "850" nor "852" is passed through
// unchanged, both fields left undefined — it's shown read-only, as today.

import type {EdiEnvelope, EdiGroup, EdiTransaction} from './envelope';
import {parse850Segments, type Parsed850} from './parser850';
import {parse852Segments, type Parsed852} from './parser852';

export interface EdiTransactionExplained extends EdiTransaction {
  /** Left undefined for a transaction whose ST01 isn't "850". */
  parsed850?: Parsed850;
  /** Left undefined for a transaction whose ST01 isn't "852". */
  parsed852?: Parsed852;
}

export interface EdiGroupExplained extends Omit<EdiGroup, 'transactions'> {
  transactions: EdiTransactionExplained[];
}

export interface EdiEnvelopeExplained extends Omit<EdiEnvelope, 'groups'> {
  groups: EdiGroupExplained[];
}

/**
 * Parses every 850 and 852 transaction in an envelope tree (lib/envelope.ts's
 * output), attaching parsed850/parsed852 to each as appropriate. Any other
 * transaction set is passed through unchanged, both fields left undefined.
 */
export function attachExplainedTransactions(
  envelope: EdiEnvelope
): EdiEnvelopeExplained {
  return {
    ...envelope,
    groups: envelope.groups.map(group => ({
      ...group,
      transactions: group.transactions.map(transaction => ({
        ...transaction,
        parsed850:
          transaction.transactionSetCode === '850'
            ? parse850Segments(transaction.segments)
            : undefined,
        parsed852:
          transaction.transactionSetCode === '852'
            ? parse852Segments(transaction.segments)
            : undefined,
      })),
    })),
  };
}
